import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import classNames from 'classnames';
import { getAttributeInfo } from '../../helpers/getAttributeInfo';
import {
    INTERFACE_STATE,
    useItemTranslations,
} from '../../PdpSharedOriginalLanguageProvider/PdpSharedOriginalLanguageProvider';

// components
import HeadingLevel from 'dibs-controlled-heading/exports/HeadingLevel';
import {
    Title,
    PdpSharedDescriptionAdditionalOptionsContent,
} from '../../PdpSharedDescriptions/PdpSharedDescriptionAdditionalOptionsContent';

// styles
import styles from './PdpRespSharedItemDetailsAdditionalOptions.scss';

// types
import { type PdpRespSharedItemDetailsAdditionalOptions_item$key as Item } from './__generated__/PdpRespSharedItemDetailsAdditionalOptions_item.graphql';
import { type PdpRespSharedItemDetailsAdditionalOptions_user$key as User } from './__generated__/PdpRespSharedItemDetailsAdditionalOptions_user.graphql';

const itemFragment = graphql`
    fragment PdpRespSharedItemDetailsAdditionalOptions_item on Item {
        isJewelry
        isCustomizable
        isDyoCustomizable
        attributes {
            customizable {
                option {
                    serviceId
                }
                text
            }
            ringResizable {
                option {
                    serviceId
                }
            }
        }
        ...PdpSharedDescriptionAdditionalOptionsContent_item
    }
`;

const userFragment = graphql`
    fragment PdpRespSharedItemDetailsAdditionalOptions_user on User {
        ...PdpSharedDescriptionAdditionalOptionsContent_user
    }
`;

export const PdpRespSharedItemDetailsAdditionalOptions: FC<{
    item: Item;
    user: User | null | undefined;
    headerClass?: string;
}> = ({ user: userRef, item: itemRef, headerClass }) => {
    const [state] = useItemTranslations();
    const item = useFragment(itemFragment, itemRef);
    const user = useFragment(userFragment, userRef);
    const itemAttributes =
        state?.interfaceState === INTERFACE_STATE.SHOW_ORIGINAL
            ? state?.data?.viewer?.item?.attributes
            : item?.attributes;
    const { isCustomizable, isDyoCustomizable, isJewelry } = item;
    const { customizationOptions, isResizable } = getAttributeInfo(itemAttributes || null);

    if ((!isCustomizable || !customizationOptions) && !isResizable && !isDyoCustomizable) {
        return null;
    }

    return (
        <div data-tn="pdp-item-description-customization-options" className={styles.container}>
            <HeadingLevel>
                {Heading => (
                    <Heading className={classNames(styles.header, headerClass)}>
                        <Title isDyoCustomizable={isDyoCustomizable} isJewelry={isJewelry} />
                    </Heading>
                )}
            </HeadingLevel>
            <PdpSharedDescriptionAdditionalOptionsContent
                user={user}
                item={item}
                collapsedHeight={60}
                buttonType="transparent"
                isResizable={isResizable}
                isDyoCustomizable={isDyoCustomizable}
                customizationOptions={customizationOptions}
            />
        </div>
    );
};
